import React, { useState } from 'react';
import { Popover, Tooltip as AntTooltip } from 'antd';
import { EllipsisOutlined } from '@ant-design/icons';
import {
  FcdNetworkIcon,
  SimfixAreasIcon,
  PredictIcon,
  TlGreenWaveIcon,
  SignalfixIcon,
  OdAnalysisIcon,
  AssignxIcon,
  MysteryClientIcon,
  SemafixIcon,
} from '../../constants/icons';
import { useAuth } from '../../contexts/AuthContext';
import { SERVICE_KEYS } from '../../constants/services';
import './ModuleSlider.css';
import { useNavigate, useLocation } from 'react-router-dom';

/**
 * ModuleSlider Component
 * Sidebar module selector with buttons for each available service.
 * Only renders buttons for services the current user has permission to access.
 *
 * Exclusive-behavior modules (deactivate all others when activated):
 *   - Predict, TL Green Wave, Assignx
 *
 * Page-navigation modules (navigate to their own route):
 *   - OD Analysis (/analysis), Signalfix (/signal-fix), Assignx (/assignx)
 */
function ModuleSlider({
  showNavbarControls,
  setShowNavbarControls,
  showSimfixAreas,
  setShowSimfixAreas,
  showPredictMode,
  setShowPredictMode,
  showTlGreenWave,
  setShowTlGreenWave,
  // Assignx active state is derived from location.pathname; props are accepted
  // by consumers that pass no-ops (e.g. Analysis, Simfix, SignalFix pages).
  // eslint-disable-next-line no-unused-vars
  showAssignx,
  // eslint-disable-next-line no-unused-vars
  setShowAssignx,
  onModuleChange,
  setNetworkViewCategory,
}) {
  const { hasService } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [moreOpen, setMoreOpen] = useState(false);

  const isOnMainPage = location.pathname === '/';
  const isOnAnalysisPage = location.pathname === '/analysis';
  const isOnSignalFixPage = location.pathname === '/signal-fix';
  const isOnAssignxPage = location.pathname === '/assignx';
  const isOnMysteryClientPage = location.pathname === '/mystery-client';
  const isOnSemafixPage = location.pathname === '/semafix';

  /**
   * Redirects to the main page and queues a module to be activated.
   */
  const redirectAndActivate = (moduleKey) => {
    sessionStorage.setItem('activateModule', moduleKey);
    navigate('/');
  };

  /** Deactivates all on-map overlay modules (used before activating an exclusive one). */
  const deactivateAllOverlays = () => {
    setShowNavbarControls(false);
    setShowSimfixAreas(false);
    setShowPredictMode(false);
    setShowTlGreenWave(false);
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: Predict (exclusive)                                        */
  /* ------------------------------------------------------------------ */
  const handlePredictClick = () => {
    if (!isOnMainPage) {
      redirectAndActivate('predict');
      return;
    }

    const activating = !showPredictMode;
    if (activating) {
      deactivateAllOverlays();
      setShowPredictMode(true);
      if (onModuleChange) onModuleChange('predict');
    } else {
      setShowPredictMode(false);
      if (onModuleChange) onModuleChange(null);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: TL Green Wave (exclusive)                                  */
  /* ------------------------------------------------------------------ */
  const handleTlGreenWaveClick = () => {
    if (!isOnMainPage) {
      redirectAndActivate('tlgreenwave');
      return;
    }

    const activating = !showTlGreenWave;
    if (activating) {
      deactivateAllOverlays();
      setShowTlGreenWave(true);
      if (onModuleChange) onModuleChange('tlgreenwave');
    } else {
      setShowTlGreenWave(false);
      if (onModuleChange) onModuleChange(null);
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: FCD Network (toggleable, deactivates exclusive modules)    */
  /* ------------------------------------------------------------------ */
  const handleFcdClick = () => {
    if (!isOnMainPage) {
      redirectAndActivate('fcd');
      return;
    }

    if (showPredictMode) setShowPredictMode(false);
    if (showTlGreenWave) setShowTlGreenWave(false);

    const activating = !showNavbarControls;
    setShowNavbarControls(activating);
    // On open, default legend/colors to speed (Low–High), matching displayed road colors
    if (activating && setNetworkViewCategory) {
      setNetworkViewCategory('velocita');
    }

    if (onModuleChange && activating) onModuleChange('fcd');
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: SIMFIX Areas (toggleable, deactivates exclusive modules)   */
  /* ------------------------------------------------------------------ */
  const handleSimfixClick = () => {
    if (!isOnMainPage) {
      redirectAndActivate('simfix');
      return;
    }

    if (showPredictMode) setShowPredictMode(false);
    if (showTlGreenWave) setShowTlGreenWave(false);

    setShowSimfixAreas(!showSimfixAreas);

    if (onModuleChange && !showSimfixAreas) onModuleChange('simfix');
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: OD Analysis (navigates to /analysis)                      */
  /* ------------------------------------------------------------------ */
  const handleODAnalysisClick = () => {
    if (!isOnAnalysisPage) navigate('/analysis');
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: Signalfix (navigates to /signal-fix)                      */
  /* ------------------------------------------------------------------ */
  const handleSignalfixClick = () => {
    if (!isOnSignalFixPage) navigate('/signal-fix');
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: Assignx (exclusive + navigates to /assignx)               */
  /*  - Activating: deactivate all overlay modules, navigate to /assignx */
  /*  - Deactivating (already on /assignx): navigate back to main map    */
  /* ------------------------------------------------------------------ */
  const handleAssignxClick = () => {
    if (isOnAssignxPage) {
      navigate('/');
    } else {
      deactivateAllOverlays();
      if (onModuleChange) onModuleChange(null);
      navigate('/assignx');
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Handler: Mystery Client (navigates to /mystery-client)             */
  /*  The dashboard opens on its own overview of every city, so one      */
  /*  click goes straight there instead of previewing a single zone.     */
  /* ------------------------------------------------------------------ */
  const handleMysteryClientClick = () => {
    if (isOnMysteryClientPage) return; // Already on the page
    navigate('/mystery-client');
  };

  const handleSemafixClick = () => {
    if (isOnSemafixPage) {
      navigate('/');
      return;
    }
    deactivateAllOverlays();
    if (onModuleChange) onModuleChange(null);
    navigate('/semafix');
  };

  const modules = [
    { key: SERVICE_KEYS.fcd_network, label: 'FCD NETX', icon: FcdNetworkIcon, active: showNavbarControls, onClick: handleFcdClick },
    { key: SERVICE_KEYS.simfix_areas, label: 'SIMFIX', icon: SimfixAreasIcon, active: showSimfixAreas, onClick: handleSimfixClick },
    { key: SERVICE_KEYS.predict, label: 'PREDICTX', icon: PredictIcon, active: showPredictMode, onClick: handlePredictClick },
    { key: SERVICE_KEYS.tl_green_wave, label: 'SYNCFIX', icon: TlGreenWaveIcon, active: showTlGreenWave, onClick: handleTlGreenWaveClick },
    { key: SERVICE_KEYS.signalfix, label: 'SIGNALFIX', icon: SignalfixIcon, active: isOnSignalFixPage, onClick: handleSignalfixClick },
    { key: SERVICE_KEYS.od_analysis, label: 'ODX', icon: OdAnalysisIcon, active: isOnAnalysisPage, onClick: handleODAnalysisClick },
    { key: SERVICE_KEYS.assignx, label: 'ASSIGNX', icon: AssignxIcon, active: isOnAssignxPage, onClick: handleAssignxClick },
    { key: SERVICE_KEYS.mystery_client, label: 'MYSTERY CLIENT', icon: MysteryClientIcon, active: isOnMysteryClientPage, onClick: handleMysteryClientClick },
    { key: SERVICE_KEYS.semafix, label: 'SEMAFIX', icon: SemafixIcon, active: isOnSemafixPage, onClick: handleSemafixClick },
  ].filter((module) => hasService(module.key));

  // Four icons stay on the rail; the rest open from the three-dot menu.
  const visibleModules = modules.slice(0, 4);
  const overflowModules = modules.slice(4);

  const moduleButton = (module, expanded = false) => {
    const button = (
      <button
        type="button"
        className={`module-slider-btn${module.active ? ' active' : ''}${expanded ? ' module-slider-more-item' : ''}`}
        onClick={() => {
          setMoreOpen(false);
          module.onClick();
        }}
        aria-label={module.label}
      >
        <img src={module.icon} alt="" className="module-icon" />
        {expanded && <span>{module.label}</span>}
      </button>
    );

    if (expanded) return React.cloneElement(button, { key: module.key });
    return (
      <AntTooltip key={module.key} title={module.label} placement="left" mouseEnterDelay={0.2} destroyOnHidden>
        {button}
      </AntTooltip>
    );
  };

  return (
    <div className="module-slider" aria-label="Moduli">
      {visibleModules.map((module) => moduleButton(module))}
      {overflowModules.length > 0 && (
        <Popover
          open={moreOpen}
          onOpenChange={setMoreOpen}
          trigger="click"
          placement="left"
          arrow={false}
          destroyOnHidden
          classNames={{ root: 'module-slider-more-popover' }}
          content={(
            <div className="module-slider-more-menu" role="menu" aria-label="Altri moduli">
              {overflowModules.map((module) => moduleButton(module, true))}
            </div>
          )}
        >
          <button
            type="button"
            className={`module-slider-btn module-slider-more-btn${overflowModules.some((module) => module.active) ? ' active' : ''}`}
            aria-label="Mostra altri moduli"
            aria-expanded={moreOpen}
          >
            <EllipsisOutlined />
          </button>
        </Popover>
      )}
    </div>
  );
}

export default ModuleSlider;
